// src/pages/Recruitment.js
import React, { useRef, useState } from "react";
import "../App.css";
import { useNavigate } from "react-router-dom";
import { useCareModal } from "../context/CareModalContext";
import {
  FaUserNurse,
  FaClock,
  FaMapMarkerAlt,
  FaClipboardList,
  FaTimes,
} from "react-icons/fa";

const roles = [
  {
    title: "Care Assistant",
    text:
      "Help clients with their daily routines, from personal care to household support. " +
      "You’ll make a real difference by ensuring comfort, dignity, and independence every day.",
    deadline: "Application deadline: 30 November 2025",
    location: "Community-based care | Various locations",
    shiftPattern: "Day shifts · Full-time / Part-time",
    about:
      "As a Care Assistant, you will provide personalised, compassionate care tailored to each client’s needs. You’ll support with washing, dressing, meal preparation, mobility and companionship, enabling clients to remain safely and comfortably in their own homes.",
    responsibilities: [
      "Provide personal care with dignity and respect",
      "Support with medication prompts (where appropriate)",
      "Assist with light household tasks and meal preparation",
      "Offer emotional support and companionship",
    ],
    requirements: [
      "Genuine passion for helping others",
      "Excellent communication and reliability",
      "Previous care experience is an advantage but not essential",
    ],
  },
  {
    title: "Support Worker",
    text:
      "Provide one-on-one assistance to individuals with disabilities or special needs. " +
      "This role focuses on empowerment, helping clients live full and independent lives.",
    deadline: "Application deadline: 05 December 2025",
    location: "Learning disability & mental health services",
    shiftPattern: "Flexible shifts · Full-time / Part-time",
    about:
      "Support Workers at Ndils Daily Care empower individuals with additional needs to achieve their personal goals, develop life skills and participate in their communities.",
    responsibilities: [
      "Encourage independence and choice in daily activities",
      "Support clients to access community services and appointments",
      "Assist with budgeting, shopping and meal planning",
      "Work collaboratively with families and professionals",
    ],
    requirements: [
      "Calm, patient and person-centred approach",
      "Ability to work as part of a multidisciplinary team",
      "Experience in support work is beneficial but not essential",
    ],
  },
  {
    title: "Night Care Assistant",
    text:
      "Deliver overnight supervision and care to ensure safety and peace of mind. " +
      "Perfect for dependable, attentive carers who thrive in quiet night-time environments.",
    deadline: "Application deadline: 15 December 2025",
    location: "Home-based night care | Various locations",
    shiftPattern: "Waking nights · Part-time / Full-time",
    about:
      "Night Care Assistants provide reassurance, supervision and hands-on support through the night, ensuring clients feel safe, comfortable and well cared for until morning.",
    responsibilities: [
      "Undertake regular welfare and safety checks",
      "Provide support with toileting and repositioning",
      "Monitor wellbeing and escalate concerns where needed",
      "Keep accurate, clear night-time records",
    ],
    requirements: [
      "Comfortable working overnight shifts",
      "High level of attention to detail and observation",
      "Previous night care experience preferred but not essential",
    ],
  },
  {
    title: "Senior Carer / Team Leader",
    text:
      "Help clients with their daily routines, from personal care to household support. " +
      "You’ll make a real difference by ensuring comfort, dignity, and independence every day.",
    deadline: "Application deadline: 20 December 2025",
    location: "Field-based & supervisory responsibilities",
    shiftPattern: "Days · Full-time",
    about:
      "The Senior Carer / Team Leader provides hands-on care while leading, mentoring and supporting a team of carers to deliver safe, high-quality, person-centred services.",
    responsibilities: [
      "Lead and support a small team of carers",
      "Complete spot checks, reviews and supervisions",
      "Contribute to care planning and risk assessments",
      "Liaise with families, professionals and management",
    ],
    requirements: [
      "Previous senior carer or supervisory experience",
      "Strong leadership, coaching and communication skills",
      "Relevant care qualifications (or working towards)",
    ],
  },
];

const Recruitment = () => {
  const navigate = useNavigate();
  const { openBookCare } = useCareModal();

  // Refs for smooth scrolling
  const rolesSectionRef = useRef(null);
  const applicationFormRef = useRef(null);

  // Modal state
  const [activeRole, setActiveRole] = useState(null);
  const [modalType, setModalType] = useState(null); // "apply" | "about" | null
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Application form state (for dropdowns)
  const [selectedRole, setSelectedRole] = useState("");
  const [availability, setAvailability] = useState("");

  const handleScrollToRoles = () => {
    if (rolesSectionRef.current) {
      rolesSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleScrollToApplicationForm = () => {
    setIsModalOpen(false);
    // slight delay so modal can close gracefully
    setTimeout(() => {
      if (applicationFormRef.current) {
        applicationFormRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 150);
  };

  const openRoleModal = (role, type) => {
    setActiveRole(role);
    setModalType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleStartApplicationFromModal = () => {
    if (activeRole) {
      setSelectedRole(activeRole.title);
    }
    handleScrollToApplicationForm();
  };

  return (
    <>
      {/* HERO */}
      <section className="hero recruitment-hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">Join Our Caring Team</h1>
          <p className="hero-subtitle">
            We&apos;re always looking for passionate individuals to make a
            difference in people&apos;s lives.
          </p>

          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={openBookCare}>
              Book For Care
            </button>
            <button
              className="btn btn-primary"
              onClick={handleScrollToRoles}
            >
              View Open Roles
            </button>
          </div>
        </div>
      </section>

      {/* WHY WORK WITH US */}
      <section className="section work-with-us">
        <h2 className="section-title">Why Work With Us</h2>
        <p className="section-subtitle">
          Training, reliability and support to help you thrive in your role.
        </p>

        <div className="values-grid">
          <article className="value-card">
            <div className="value-icon">🎓</div>
            <h3>Training</h3>
            <p>
              We invest in our carers through continuous, hands-on training and
              professional development. Our goal is to empower every team member
              with the knowledge and confidence to deliver the highest quality
              care.
            </p>
          </article>

          <article className="value-card">
            <div className="value-icon">🕒</div>
            <h3>Reliability</h3>
            <p>
              At Ndils Daily Care, reliability means showing up — every time,
              with consistency and compassion. Our clients and communities trust
              us because we take pride in being dependable, punctual, and
              professional.
            </p>
          </article>

          <article className="value-card">
            <div className="value-icon">💗</div>
            <h3>Support</h3>
            <p>
              We believe care begins with our team — that&apos;s why we provide
              constant guidance, encouragement, and open communication. From
              onboarding to ongoing growth, you&apos;ll always have the support
              you need to thrive in your role.
            </p>
          </article>
        </div>
      </section>

      {/* CURRENT ROLES */}
      <section
        className="section roles-section"
        ref={rolesSectionRef}
        id="current-roles"
      >
        <h2 className="section-title">Current Roles</h2>

        <div className="roles-grid">
          {roles.map((role) => (
            <article key={role.title} className="role-card">
              <h3>{role.title}</h3>
              <p>{role.text}</p>

              <p className="role-deadline">
                <FaClock
                  style={{ marginRight: "0.4rem", verticalAlign: "middle" }}
                />
                <span>{role.deadline}</span>
              </p>

              <div className="role-actions">
                <button
                  className="role-btn"
                  onClick={() => openRoleModal(role, "apply")}
                >
                  Apply
                </button>
                <button
                  className="role-btn role-btn-outline"
                  onClick={() => openRoleModal(role, "about")}
                >
                  About the Job
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        className="section application-form"
        ref={applicationFormRef}
        id="application-form"
      >
        <h2 className="section-title">Application Form</h2>

        <div className="app-form-grid">
          <input
            type="text"
            placeholder="Full Name"
            className="app-form-field"
          />
          <input
            type="email"
            placeholder="Email Address"
            className="app-form-field"
          />
          <input
            type="tel"
            placeholder="Phone Number"
            className="app-form-field"
          />
          <input
            type="text"
            placeholder="Location"
            className="app-form-field"
          />

          {/* Role Applying For - dropdown populated from current roles */}
          <select
            className="app-form-field"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            <option value="">Role Applying For</option>
            {roles.map((role) => (
              <option key={role.title} value={role.title}>
                {role.title}
              </option>
            ))}
          </select>

          {/* Availability - dropdown that behaves accordingly */}
          <select
            className="app-form-field"
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
          >
            <option value="">Availability</option>
            <option value="immediately">Immediately</option>
            <option value="1-week">Within 1 week</option>
            <option value="2-weeks">Within 2 weeks</option>
            <option value="weekdays">Weekdays only</option>
            <option value="nights-weekends">Nights & Weekends</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>

        <button className="btn app-submit">Submit</button>
      </section>

      {/* SUPERB MODAL POPUP */}
      {isModalOpen && activeRole && (
        <div
          className="care-modal-backdrop"
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.75), rgba(15,23,42,0.85))",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
            zIndex: 9999,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="care-modal-title"
            className="care-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "600px",
              background: "#ffffff",
              borderRadius: "18px",
              padding: "1.75rem 1.5rem 1.5rem",
              boxShadow: "0 22px 45px rgba(15,23,42,0.35)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              transform: "translateY(0)",
              opacity: 1,
              transition: "all 0.28s ease-out",
            }}
          >
            {/* Accent gradient bar / logo area */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 55%), radial-gradient(circle at bottom right, rgba(236,72,153,0.12), transparent 55%)",
              }}
            />

            <header
              className="care-modal-header"
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  minWidth: 0,
                }}
              >
                {/* Logo-style badge */}
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "999px",
                    background:
                      "linear-gradient(135deg, #2563eb, #7c3aed, #ec4899)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1rem",
                    flexShrink: 0,
                    boxShadow: "0 10px 25px rgba(37,99,235,0.45)",
                  }}
                >
                  ND
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                    minWidth: 0,
                  }}
                >
                  <h3
                    id="care-modal-title"
                    style={{
                      fontSize: "1.1rem",
                      lineHeight: 1.25,
                      fontWeight: 600,
                      color: "#0f172a",
                      margin: 0,
                    }}
                  >
                    {modalType === "apply"
                      ? `Apply for ${activeRole.title}`
                      : activeRole.title}
                  </h3>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      margin: 0,
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        padding: "0.15rem 0.5rem",
                        borderRadius: "999px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        fontWeight: 500,
                      }}
                    >
                      <FaUserNurse />
                      Ndils Daily Care Careers
                    </span>

                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        padding: "0.15rem 0.5rem",
                        borderRadius: "999px",
                        backgroundColor: "#fef2f2",
                        color: "#b91c1c",
                        fontWeight: 500,
                      }}
                    >
                      <FaClock />
                      {activeRole.deadline}
                    </span>
                  </p>
                </div>
              </div>

              <button
                onClick={closeModal}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "rgba(15,23,42,0.04)",
                  borderRadius: "999px",
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <FaTimes style={{ fontSize: "0.95rem", color: "#0f172a" }} />
              </button>
            </header>

            {/* Body */}
            <div
              className="care-modal-body"
              style={{
                position: "relative",
                display: "grid",
                gap: "0.85rem",
                maxHeight: "60vh",
                overflowY: "auto",
                paddingRight: "0.1rem",
              }}
            >
              {modalType === "about" && (
                <>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      color: "#4b5563",
                      margin: 0,
                    }}
                  >
                    {activeRole.about}
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gap: "0.65rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          padding: "0.25rem 0.55rem",
                          borderRadius: "999px",
                          backgroundColor: "#ecfeff",
                          color: "#0891b2",
                          fontWeight: 500,
                        }}
                      >
                        <FaMapMarkerAlt />
                        {activeRole.location}
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          padding: "0.25rem 0.55rem",
                          borderRadius: "999px",
                          backgroundColor: "#f5f3ff",
                          color: "#6d28d9",
                          fontWeight: 500,
                        }}
                      >
                        <FaClock />
                        {activeRole.shiftPattern}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: "0.55rem",
                        padding: "0.75rem",
                        borderRadius: "12px",
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          color: "#111827",
                        }}
                      >
                        <FaClipboardList />
                        Key Responsibilities
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "1.1rem",
                          display: "grid",
                          gap: "0.25rem",
                          color: "#4b5563",
                        }}
                      >
                        {activeRole.responsibilities.map((item) => (
                          <li key={item} style={{ fontSize: "0.85rem" }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: "0.55rem",
                        padding: "0.75rem",
                        borderRadius: "12px",
                        backgroundColor: "#fdf2ff",
                        border: "1px solid #f5d0fe",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#581c87",
                        }}
                      >
                        What We&apos;re Looking For
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "1.1rem",
                          display: "grid",
                          gap: "0.25rem",
                          color: "#6b21a8",
                        }}
                      >
                        {activeRole.requirements.map((item) => (
                          <li key={item} style={{ fontSize: "0.85rem" }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              )}

              {modalType === "apply" && (
                <>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      color: "#4b5563",
                      margin: 0,
                    }}
                  >
                    Ready to apply for the{" "}
                    <strong>{activeRole.title}</strong> role? You can complete
                    the quick application form below on this page. Your details
                    will help us understand your experience, availability and
                    preferred working pattern.
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gap: "0.55rem",
                      padding: "0.75rem",
                      borderRadius: "12px",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      fontSize: "0.85rem",
                      color: "#1d4ed8",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      Before You Apply
                    </h4>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "1.1rem",
                        display: "grid",
                        gap: "0.25rem",
                      }}
                    >
                      <li>Ensure your contact details are up to date.</li>
                      <li>
                        Be ready to tell us about any relevant experience or
                        training.
                      </li>
                      <li>
                        Think about your preferred working pattern and
                        availability.
                      </li>
                    </ul>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      fontSize: "0.8rem",
                    }}
                  >
                    <span
                      style={{
                        padding: "0.25rem 0.55rem",
                        borderRadius: "999px",
                        backgroundColor: "#fef2f2",
                        color: "#b91c1c",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaClock />
                      {activeRole.deadline}
                    </span>
                    <span
                      style={{
                        padding: "0.25rem 0.55rem",
                        borderRadius: "999px",
                        backgroundColor: "#ecfdf5",
                        color: "#047857",
                      }}
                    >
                      Shortlisted candidates will be contacted for interview.
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Footer Buttons */}
            <footer
              className="care-modal-footer"
              style={{
                position: "relative",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: "0.5rem",
                marginTop: "0.25rem",
              }}
            >
              <button
                onClick={closeModal}
                style={{
                  borderRadius: "999px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  padding: "0.5rem 1rem",
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  color: "#111827",
                }}
              >
                Close
              </button>

              <button
                onClick={handleStartApplicationFromModal}
                style={{
                  borderRadius: "999px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #2563eb, #7c3aed, #ec4899)",
                  padding: "0.55rem 1.3rem",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "#ffffff",
                  boxShadow: "0 12px 25px rgba(37,99,235,0.5)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  flexShrink: 0,
                }}
              >
                {modalType === "about" ? "Apply for this Role" : "Start Application"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default Recruitment;
